import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronUp, Link2, Save } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import { useEventStore } from '@/store'
import { createDefaultEvent } from '@/lib/templates'
import type { EventConfig } from '@/types'
import { applySettings, extractSettings, SETTINGS_SECTIONS, type SettingsSection, type SettingsValues } from '../../../server/lib/eventSettings'
import SettingsSectionForm from './SettingsSectionForm'
import { settingsSummary } from '@/lib/settingsPresentation'

interface DefaultsResponse {
  defaults: SettingsValues
  revision: number
  inheritingCounts: Record<SettingsSection, number>
}
const groups: Array<{ label: string; sections: SettingsSection[] }> = [
  { label: 'av', sections: ['camera', 'audio', 'photoLight'] },
  { label: 'display', sections: ['screensaver', 'slideshow'] },
  { label: 'capture', sections: ['captureBehavior', 'dataSaver'] },
]
export default function EventDefaults() {
  const { t } = useI18n()
  const loadMessage = t('inheritance.loadError')
  const [params] = useSearchParams()
  const selected = params.get('section') as SettingsSection | null
  const [open, setOpen] = useState<SettingsSection | null>(selected && SETTINGS_SECTIONS.includes(selected) ? selected : null)
  const [saved, setSaved] = useState<DefaultsResponse | null>(null)
  const [draft, setDraft] = useState<SettingsValues | null>(null)
  const [events, setEvents] = useState<Array<{ id: string; name: string }>>([])
  const [copyId, setCopyId] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const template = useMemo(() => createDefaultEvent(), [])
  const load = useCallback(async () => {
    setError('')
    try {
      const [settingsResponse, eventsResponse] = await Promise.all([fetch('/api/settings/event-defaults'), fetch('/api/events')])
      if (!settingsResponse.ok || !eventsResponse.ok) throw new Error(loadMessage)
      const [data, list] = await Promise.all([settingsResponse.json() as Promise<DefaultsResponse>, eventsResponse.json()])
      setSaved(data); setDraft(data.defaults); setEvents(list)
      useEventStore.getState().setDefaults(data.defaults, data.revision)
    } catch (e) { setError(e instanceof Error ? e.message : loadMessage) }
  }, [loadMessage])
  useEffect(() => { void load() }, [load])
  const changed = draft && saved ? SETTINGS_SECTIONS.filter(k => JSON.stringify(draft[k]) !== JSON.stringify(saved.defaults[k])) : []
  const event = draft ? applySettings(template, draft) : null
  const copyFromEvent = async () => {
    if (!copyId) return
    setBusy(true); setError(''); setMessage('')
    try {
      const response = await fetch(`/api/events/${encodeURIComponent(copyId)}`)
      if (!response.ok) throw new Error(t('inheritance.loadError'))
      const data = await response.json()
      setDraft(extractSettings(data.config as EventConfig))
      setMessage(t('inheritance.copyPreview'))
    } catch (e) { setError(e instanceof Error ? e.message : t('inheritance.loadError')) }
    finally { setBusy(false) }
  }
  const save = async () => {
    if (!draft || !saved) return
    setBusy(true); setError(''); setMessage('')
    try {
      const response = await fetch('/api/settings/event-defaults', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ defaults: draft, revision: saved.revision }) })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || t('inheritance.saveError'))
      setSaved(data); setDraft(data.defaults)
      useEventStore.getState().setDefaults(data.defaults, data.revision)
      setMessage(t('inheritance.saved'))
    } catch (e) { setError(e instanceof Error ? e.message : t('inheritance.saveError')) }
    finally { setBusy(false) }
  }
  return <div className="space-y-6">
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-2">
      <h2 className="flex items-center gap-2 font-semibold"><Link2 size={18} />{t('inheritance.defaultsTitle')}</h2>
      <p className="text-sm text-muted-foreground">{t('inheritance.defaultsDescription')}</p>
      <p className="text-xs text-muted-foreground">{t('inheritance.reloadNote')}</p>
    </div>
    {error && <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">{error} <button onClick={() => void load()} className="ml-2 underline">{t('inheritance.reload')}</button></div>}
    {message && <p role="status" className="text-sm text-primary">{message}</p>}
    {!draft || !saved || !event ? (!error && <p role="status">{t('common.loading')}</p>) : <>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border p-4">
        <label className="flex-1 min-w-48 space-y-2 text-sm"><span>{t('inheritance.copyFromEvent')}</span>
          <select aria-label={t('inheritance.copyFromEvent')} value={copyId} onChange={e => setCopyId(e.target.value)} className="block w-full rounded-lg border border-input bg-background px-3 py-2">
            <option value="">{t('inheritance.chooseEvent')}</option>{events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => void copyFromEvent()} disabled={!copyId || busy} className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50">{t('inheritance.previewCopy')}</button>
      </div>
      <fieldset disabled={busy} className="space-y-6">
        {groups.map(group => <div key={group.label} className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground">{t(`inheritance.groups.${group.label}`)}</h2>
          {group.sections.map(section => <section key={section} id={section} className="rounded-xl border border-border bg-card/40">
            <button type="button" aria-expanded={open === section} aria-controls={`defaults-${section}`} onClick={() => setOpen(current => current === section ? null : section)} className="flex w-full items-center justify-between gap-4 p-5 text-left hover:bg-muted/30">
              <span className="space-y-1"><span className="block font-semibold">{t(`inheritance.sections.${section}`)}</span><span className="block text-sm text-muted-foreground">{settingsSummary(section, draft, t)}</span><span className="block text-xs text-muted-foreground">{t('inheritance.eventCount', { count: saved.inheritingCounts[section] })}</span></span>
              {open === section ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {open === section && <div id={`defaults-${section}`} className="border-t border-border p-5">
              {['camera', 'audio', 'screensaver'].includes(section) && <p className="mb-4 text-xs text-amber-500">{t('inheritance.deviceNote')}</p>}
              <SettingsSectionForm section={section} event={event} onChange={updates => {
                const values = extractSettings({ ...event, ...updates })
                setDraft(current => current ? { ...current, [section]: values[section] } : current)
                setMessage('')
              }} />
            </div>}
          </section>)}
        </div>)}
      </fieldset>
      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-md">
        <div className="text-xs text-muted-foreground space-y-1">
          {changed.length ? changed.map(section => <p key={section}>{t(`inheritance.sections.${section}`)}: {t('inheritance.eventCount', { count: saved.inheritingCounts[section] })}</p>) : <p>{t('inheritance.noChanges')}</p>}
        </div>
        <div className="flex gap-2">
          <button type="button" disabled={busy || !changed.length} onClick={() => { setDraft(saved.defaults); setMessage(''); setError('') }} className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50">{t('common.cancel')}</button>
          <button type="button" disabled={busy || !changed.length} onClick={() => void save()} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"><Save size={15} />{t(busy ? 'editor.saving' : 'inheritance.saveDefaults')}</button>
        </div>
      </div>
    </>}
  </div>
}
